import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { site } from "@/config/site";
import { openWhatsApp, telUrl, trackPhoneClick } from "@/lib/whatsapp";
import { trackEvent, EVENTS } from "@/lib/analytics";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/who-we-help", label: "Who We Help" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export function Wordmark({ light = false, className = "" }) {
  // Official logo lockup. On dark backgrounds place it on a white pill so the
  // white-background asset stays legible.
  return (
    <Link to="/" className={`inline-flex items-center ${className}`} data-testid="site-wordmark">
      <span className={light ? "rounded-xl bg-white px-2.5 py-1.5 shadow-sm" : ""}>
        <img
          src={site.logo.header}
          alt="Bonsai Financial"
          className={light ? "h-9 w-auto" : "h-10 w-auto"}
          loading="eager"
        />
      </span>
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const goConsult = () => {
    trackEvent(EVENTS.CONSULTATION_REQUEST, { from: "header" });
    navigate("/assessment");
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-[#F9F8F5]/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Wordmark />

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[#133827]" : "text-[#526057] hover:text-[#133827]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <Button
            variant="outline"
            onClick={() => openWhatsApp("general")}
            data-testid="nav-whatsapp-btn"
            className="border-[#25D366] text-[#1a8a44] hover:bg-[#25D366]/10"
          >
            WhatsApp Us
          </Button>
          <Button
            onClick={goConsult}
            data-testid="nav-request-consultation-btn"
            className="bg-[#133827] text-white hover:bg-[#0d261a]"
          >
            Request a Consultation
          </Button>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={telUrl()}
            onClick={trackPhoneClick}
            data-testid="mobile-phone-btn"
            aria-label="Call Bonsai Financial"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-[#133827]"
          >
            <Phone className="h-5 w-5" />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="mobile-menu-btn"
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#133827] text-white"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-[#F9F8F5] p-0">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                <Wordmark />
                <button aria-label="Close menu" onClick={() => setOpen(false)} data-testid="mobile-menu-close">
                  <X className="h-5 w-5 text-[#526057]" />
                </button>
              </div>
              <nav className="flex flex-col p-4">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `rounded-lg px-3 py-3 text-base font-medium ${
                        isActive ? "bg-[#133827]/5 text-[#133827]" : "text-[#526057]"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  <Button onClick={goConsult} data-testid="mobile-request-consultation-btn" className="bg-[#133827] text-white hover:bg-[#0d261a]">
                    Request a Consultation
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { openWhatsApp("general"); setOpen(false); }}
                    data-testid="mobile-whatsapp-btn"
                    className="border-[#25D366] text-[#1a8a44]"
                  >
                    WhatsApp Us
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Trust strip */}
      <div className="border-t border-slate-200/70 bg-[#133827]">
        <div className="mx-auto max-w-7xl px-4 py-1.5 text-center sm:px-6 lg:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C68B59]" data-testid="trust-line">
            {site.credentials.trustLine}
          </p>
        </div>
      </div>
    </header>
  );
}
